/**
 * Subscription Status Banner
 * 
 * Displays the current subscription status for contractors:
 * - Founder Promo (special unlimited access)
 * - Demo Access (admin-granted)
 * - Free Trial (7-day Pro trial, no credit card)
 * - Paid Subscription (active)
 * - Expired/Inactive prompts
 */

import { 
  Crown, Clock, Beaker, AlertTriangle, Star,
  CreditCard, Gift, Zap, CheckCircle, Sparkles, Infinity
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";

export default function SubscriptionStatusBanner({ subscriptionData, onUpgrade, onStartTrial }) {
  if (!subscriptionData) return null;
  
  const {
    subscription_status,
    subscription_type,
    subscription_plan,
    is_demo_account,
    credit_balance,
    can_access_leads,
    access_message,
    show_upgrade_prompt,
    trial_used,
    // Demo-specific
    demo_days_remaining,
    demo_credits_remaining,
    demo_credits_limit,
    // Trial-specific (new 7-day Pro trial)
    trial_days_remaining,
    trial_credits_remaining,
    trial_credits_granted,
    trial_plan,
    // Promo-specific (founder promo)
    promo_active,
    promo_type,
    promo_days_remaining,
    promo_end_at,
    is_unlimited_credits
  } = subscriptionData;

  // Founder Promo Banner (special unlimited access)
  if (subscription_type === "promo" && promo_active) {
    return (
      <div 
        className="bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 border-2 border-amber-300 rounded-xl p-4 mb-6 shadow-sm"
        data-testid="founder-promo-banner"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center flex-shrink-0 shadow-md">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-amber-900">Founder Access</h3>
                <Badge className="bg-amber-200 text-amber-800 text-xs border border-amber-300">
                  <Crown className="w-3 h-3 mr-1" /> PRO
                </Badge>
              </div>
              <p className="text-amber-700 text-sm">
                Special founder privileges – Full Pro access with unlimited credits
              </p>
              
              {/* Time & Credits Display */}
              <div className="flex flex-wrap gap-4 mt-2 text-sm">
                <div className="flex items-center gap-1 text-amber-600">
                  <Clock className="w-4 h-4" />
                  <span>{promo_days_remaining || 0} days remaining</span>
                </div>
                <div className="flex items-center gap-1 text-amber-600">
                  <Infinity className="w-4 h-4" />
                  <span className="font-semibold">Unlimited Credits</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 bg-amber-100 px-3 py-2 rounded-lg border border-amber-200">
            <Zap className="w-5 h-5 text-amber-500" />
            <span className="font-bold text-lg text-amber-800">∞</span>
            <span className="text-sm text-amber-600">credits</span>
          </div>
        </div>
      </div>
    );
  }

  // Demo Access Banner
  if (subscription_type === "demo") {
    const creditsPercent = demo_credits_limit > 0 
      ? ((demo_credits_limit - (demo_credits_remaining || 0)) / demo_credits_limit) * 100 
      : 0;
    
    return (
      <div 
        className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4 mb-6"
        data-testid="demo-subscription-banner"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center flex-shrink-0">
              <Beaker className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-purple-900">Demo Access</h3>
                <Badge className="bg-purple-200 text-purple-800 text-xs">
                  {subscription_plan?.toUpperCase() || "STARTER"}
                </Badge>
              </div>
              <p className="text-purple-700 text-sm">
                No payment required – Practice the platform freely
              </p>
              
              {/* Credits & Time Display */}
              <div className="flex flex-wrap gap-4 mt-2 text-sm">
                {demo_days_remaining !== undefined && (
                  <div className="flex items-center gap-1 text-purple-600">
                    <Clock className="w-4 h-4" />
                    <span>{demo_days_remaining} days left</span>
                  </div>
                )}
                <div className="flex items-center gap-1 text-purple-600">
                  <Zap className="w-4 h-4" />
                  <span>{demo_credits_remaining || 0} / {demo_credits_limit || 50} credits</span>
                </div>
              </div>
              
              {/* Credits Progress Bar */}
              <div className="mt-2 max-w-xs">
                <Progress value={creditsPercent} className="h-2 bg-purple-200" />
              </div>
            </div>
          </div>
          
          {show_upgrade_prompt && (
            <Button
              onClick={onUpgrade}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="upgrade-btn"
            >
              <Crown className="w-4 h-4 mr-2" /> Upgrade to Paid
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Free Trial Banner (7-day Pro trial)
  if (subscription_type === "trial") {
    const trialCredits = trial_credits_granted || 25;
    const creditsUsed = trialCredits - (trial_credits_remaining || 0);
    const creditsPercent = trialCredits > 0 ? (creditsUsed / trialCredits) * 100 : 0;
    const isExpiringSoon = trial_days_remaining !== undefined && trial_days_remaining <= 2;
    const isPro = (trial_plan || subscription_plan || "").toLowerCase() === "pro";
    
    return (
      <div 
        className={`border rounded-xl p-4 mb-6 ${
          isExpiringSoon 
            ? "bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200" 
            : "bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200"
        }`}
        data-testid="trial-subscription-banner"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isExpiringSoon ? "bg-amber-500" : "bg-purple-500"
            }`}>
              {isPro ? <Star className="w-5 h-5 text-white" /> : <Gift className="w-5 h-5 text-white" />}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className={`font-semibold ${isExpiringSoon ? "text-amber-900" : "text-purple-900"}`}>
                  {isPro ? "Pro Trial" : "Free Trial"}
                </h3>
                <Badge className={`text-xs ${
                  isExpiringSoon 
                    ? "bg-amber-200 text-amber-800" 
                    : "bg-purple-200 text-purple-800"
                }`}>
                  {isPro ? "PRO ACCESS" : (subscription_plan?.toUpperCase() || "STARTER")}
                </Badge>
              </div>
              <p className={`text-sm ${isExpiringSoon ? "text-amber-700" : "text-purple-700"}`}>
                {isExpiringSoon 
                  ? "Your Pro trial is ending soon – Subscribe to keep access" 
                  : isPro 
                    ? "Full Pro access – Leads up to $25,000 • No credit card required"
                    : "Explore the platform – No credit card required"}
              </p>
              
              {/* Time & Credits Display */}
              <div className="flex flex-wrap gap-4 mt-2 text-sm">
                <div className={`flex items-center gap-1 ${isExpiringSoon ? "text-amber-600" : "text-purple-600"}`}>
                  <Clock className="w-4 h-4" />
                  <span className={isExpiringSoon ? "font-semibold" : ""}>
                    {trial_days_remaining || 0} day{trial_days_remaining !== 1 ? 's' : ''} left
                  </span>
                </div>
                <div className={`flex items-center gap-1 ${isExpiringSoon ? "text-amber-600" : "text-purple-600"}`}>
                  <Zap className="w-4 h-4" />
                  <span>{trial_credits_remaining || 0} / {trialCredits} credits</span>
                </div>
              </div>
              
              {/* Credits Progress Bar */}
              <div className="mt-2 max-w-xs">
                <Progress 
                  value={creditsPercent} 
                  className={`h-2 ${isExpiringSoon ? "bg-amber-200" : "bg-purple-200"}`} 
                />
              </div>
            </div>
          </div>
          
          <Button
            onClick={onUpgrade}
            className={isExpiringSoon 
              ? "bg-amber-600 hover:bg-amber-700 text-white" 
              : "bg-purple-600 hover:bg-purple-700 text-white"
            }
            data-testid="upgrade-btn"
          >
            <Crown className="w-4 h-4 mr-2" /> Upgrade Now
          </Button>
        </div>
      </div>
    );
  }

  // Paid Subscription Banner
  if (subscription_type === "paid" && subscription_status === "active") {
    return (
      <div 
        className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-4 mb-6"
        data-testid="paid-subscription-banner"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#0FA3B1] flex items-center justify-center">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-[#1F2933]">
                  {subscription_plan?.charAt(0).toUpperCase() + subscription_plan?.slice(1)} Plan
                </h3>
                <Badge className="bg-[#0FA3B1]/10 text-[#0FA3B1] text-xs">
                  <CheckCircle className="w-3 h-3 mr-1" /> Active
                </Badge>
              </div>
              <p className="text-[#1F2933]/60 text-sm">
                Full access to all leads and features
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-[#1F2933]">
            <Zap className="w-5 h-5 text-[#0FA3B1]" />
            <span className="font-bold text-lg">{credit_balance || 0}</span>
            <span className="text-sm text-[#1F2933]/60">credits</span>
          </div>
        </div>
      </div>
    );
  }

  // Inactive/Expired Banner
  if (!can_access_leads || show_upgrade_prompt) {
    const trialAlreadyUsed = trial_used === true;
    
    return (
      <div 
        className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-300 rounded-xl p-4 mb-6"
        data-testid="inactive-subscription-banner"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-400 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1F2933]">
                {trialAlreadyUsed ? "Trial Ended" : "No Active Subscription"}
              </h3>
              <p className="text-[#1F2933]/60 text-sm">
                {access_message || (trialAlreadyUsed 
                  ? "Your Pro trial has ended. Subscribe to continue accessing leads."
                  : "Subscribe to access leads and grow your business")}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {!trialAlreadyUsed && (
              <Button
                variant="outline"
                onClick={onStartTrial || onUpgrade}
                className="border-purple-500 text-purple-600 hover:bg-purple-50"
                data-testid="start-trial-btn"
              >
                <Gift className="w-4 h-4 mr-2" /> Start Pro Trial
              </Button>
            )}
            <Button
              onClick={onUpgrade}
              className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white"
              data-testid="subscribe-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" /> Subscribe
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
